/**
 * Created by Ning Jiang on 10/10/2018.
 */

import {GameConfig} from "@nolimitcity/slot-game/bin/core/gameconfig/GameConfig";
import {SlotGame} from "@nolimitcity/slot-game/bin/core/SlotGame";
import {BitmapTextStyleOptions} from "@nolimitcity/slot-game/bin/core/text/BitmapTextStyleOptions";
import {MathHelper} from "@nolimitcity/slot-game/bin/core/utils/MathHelper";
import {WinRatioType} from "@nolimitcity/slot-game/bin/core/winpresentation/WinRatio";
import {BitmapWinCountUp} from "@nolimitcity/slot-game/bin/game/winpresentation/countup/BitmapWinCountUp";
import {Elastic, Power1, Timeline, TimelineLite, TweenLite} from "gsap";
import {ThorSoundConfig} from "../../ThorSoundConfig";
import {ThorGameConfig} from "../../ThorGameConfig";

export class ThorInitialWinCountUp extends BitmapWinCountUp {

    private _isCountUpSoundPlaying:boolean = false;
    private _hasCountUpEndSoundPlayed:boolean = false;
    private _currentWinRatio:WinRatioType;

    constructor(bitmapStyle:BitmapTextStyleOptions) {
        super(bitmapStyle);
    }

    private getScale(multiplier:number):number {
        return 1 + (multiplier - 1) * 0.1;
    }


    public getCountUpAnimation(from:number, to:number, duration:number, multiplier:number, calledFromOutro: boolean, canPlayOutroEndSound: boolean):Timeline {
        const oriNumber:number = MathHelper.floorToDecimals(to / multiplier, 2);
        const timeline:TimelineLite = new TimelineLite();
        timeline.add([
            () => {
                this._countUp.maxTextWidth = GameConfig.instance.REEL_AREA_WIDTH * 0.9 / this.getScale(multiplier);
                this._countUp.alpha = 1;
                this._countUp.visible = true;
                this._countUpContainer.scale.set(1, 1);
                if(duration > 0.01) {
                    SlotGame.sound.playEffect(ThorSoundConfig.instance.COUNTUP);
                    this._isCountUpSoundPlaying = true;
                }
            },
            this._countUp.getCountUpAnimation(from, oriNumber, {duration : duration, ease : Power1.easeIn})
        ]);

        timeline.add(() => this.stopCountUpSound(calledFromOutro, canPlayOutroEndSound));

        if(multiplier > 1) {
            for(let m:number = 1; m <= multiplier; m++) {
                const showNumber:number = oriNumber * m;
                const scale:number = this.getScale(m);
                timeline.add([
                    this._countUp.getCountUpAnimation(showNumber, showNumber, {duration : 0.01}),
                    TweenLite.to(this._countUpContainer.scale, 0.3,
                        {
                            x : scale,
                            y : scale,
                            ease : Elastic.easeOut.config(2, 1)
                        }),
                ]);
                timeline.add(TweenLite.to(this, 0.3, {}));
            }
        }

        return timeline;
    }

    private stopCountUpSound(calledFromOutro: boolean, canPlayOutroEndSound: boolean):void {
        if(this._isCountUpSoundPlaying) {
            SlotGame.sound.stopEffect(ThorSoundConfig.instance.COUNTUP);
            this._isCountUpSoundPlaying = false;
        }

        if(!this._hasCountUpEndSoundPlayed) {
            // #311
            if ((calledFromOutro && canPlayOutroEndSound) || (!calledFromOutro && !ThorGameConfig.instance.WIN_BELOW_STAKE)) {
                const endSoundTL: TimelineLite = new TimelineLite();
                endSoundTL.add(() => SlotGame.sound.playEffect(ThorSoundConfig.instance.COUNTUP_ENDS), "+=" + this.getEndSoundDelay(this._currentWinRatio));
            }
            this._hasCountUpEndSoundPlayed = true;
        }
    }

    public abort(endValue:number, isAborted:boolean, multiplier:number, isBigWin:boolean):Timeline | null {
        if(isBigWin) {
            return null;
        }

        return super.abort(endValue, isAborted, multiplier);
    }

    protected getAbortAnimation(endValue:number, multiplier:number):Timeline {
        const timeline:TimelineLite = new TimelineLite();

        timeline.add([
            () => {
                this._countUp.maxTextWidth = GameConfig.instance.REEL_AREA_WIDTH * 0.9 / this.getScale(multiplier);
                this._countUp.alpha = 1;
                this._countUp.visible = true;
                this._countUpContainer.scale.set(1, 1);
            },
            this._countUp.getCountUpAnimation(endValue, endValue, {duration : 0.01})
        ]);
        if(multiplier > 1) {
            const scale:number = this.getScale(multiplier);
            timeline.add(TweenLite.to(this._countUpContainer.scale, 0.2, {
                x : scale,
                y : scale,
                ease : Elastic.easeOut.config(2, 1)
            }), 0)
        }

        return timeline;
    }

    public getEndingAnimation(endValue:number):Timeline {
        const timeline:TimelineLite = new TimelineLite({
            onComplete : () => {
                this._isCountUpSoundPlaying = false;
                this._hasCountUpEndSoundPlayed = false;
            }
        });
        timeline.add(() => this.stopCountUpSound(false, false));
        timeline.add(this._countUp.getCountUpAnimation(endValue, endValue, {duration : 0.01}));
        timeline.add(TweenLite.to(this._countUp, 0.5, {alpha : 0}), `+=0.8`); // stay a while after count up.

        return timeline;
    }

    set currentWinRatio(value:WinRatioType) {
        this._currentWinRatio = value;
    }

    private getEndSoundDelay(winType:WinRatioType):number {
        switch(winType) {
            case WinRatioType.LARGE :
                return 0;

            case WinRatioType.MEDIUM :
                return 0;

            case WinRatioType.SMALL :
                return 0.274;

            default :
                return 0.274;
        }
    }

    public currentValue(number:number){
        this._countUp.currentValue=number;
    }
}